import { has } from 'lodash';
import { useEffect, useLayoutEffect, useRef, RefObject } from 'react';

type ElementOrWindow<T extends Element> = Window | T | null | undefined;
type ElementOrRef<T extends Element> =
  | ElementOrWindow<T>
  | RefObject<ElementOrWindow<T>>;
type EventHandler<
  T extends Element,
  E extends keyof GlobalEventHandlersEventMap
> = (evt: React.SyntheticEvent<T, GlobalEventHandlersEventMap[E]>) => void;

function isRef<T extends Element>(
  elementOrRef: ElementOrRef<T>
): elementOrRef is RefObject<ElementOrWindow<T>> {
  return has(elementOrRef, 'current');
}

function useEventListener<
  T extends Element,
  E extends keyof GlobalEventHandlersEventMap
>(
  eventName: E,
  callback: EventHandler<T, E>,
  elementOrRef: ElementOrRef<T> = window
) {
  const element = useRef<ElementOrWindow<T>>();
  const savedCallback = useRef<EventHandler<T, E>>();

  useEffect(() => {
    if (element.current) return;

    if (isRef(elementOrRef)) {
      element.current = elementOrRef.current;
    } else {
      element.current = elementOrRef;
    }
  }, [elementOrRef]);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useLayoutEffect(() => {
    if (element.current == null || savedCallback.current == null) return;

    const eventListener: EventHandler<T, E> = (event) =>
      savedCallback.current?.(event);

    element.current.addEventListener(
      eventName,
      eventListener as unknown as EventListener
    );

    return function cleanup() {
      return element?.current?.removeEventListener(
        eventName,
        eventListener as unknown as EventListener
      );
    };
  }, [eventName, element.current]);
}

export default useEventListener;
